import { useState, useMemo, ChangeEvent, FC, useEffect } from "react"
import classNames from "classnames"
import { Icon, IconColor } from "@/components/shared/Icon"
import { debounce } from "@/utils/lodash"
import "./index.scss"

type SearchInputProps = {
   className?: string
   name?: string
   onSearch: (value: string) => void
   onSearchIconClick?: () => void
   delay?: number
   placeholder?: string
   disabled?: boolean
   value?: string
   error?: boolean
   onBlur?: (field: string) => void
   onBulkPaste?: (rows: string[]) => void
}

const SearchInput: FC<SearchInputProps> = ({
   className,
   name,
   onSearch,
   onSearchIconClick,
   delay = 300,
   placeholder = "Search...",
   disabled = false,
   value,
   error,
   onBlur,
   onBulkPaste,
}) => {
   const [inputValue, setInputValue] = useState(value || "")
   const [isFocused, setIsFocused] = useState(false)

   useEffect(() => {
      setInputValue(value || "")
   }, [value])

   const debouncedSearch = useMemo(
      () => debounce(onSearch, delay),
      [onSearch, delay]
   )

   const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setInputValue(value)
      debouncedSearch(value)
   }

   return (
      <div className={classNames("search-input", className)}>
         <span
            className={classNames("search-input__input", {
               "search-input__input--disabled": disabled,
               "search-input__input--focused": isFocused,
               "search-input__input--error": error,
            })}
         >
            <Icon
               className="search-input__search-icon"
               name="search"
               size="12"
               color={IconColor.GRAY}
               onClick={() => onSearchIconClick?.()}
            />
            <input
               id="search-bar"
               name={name}
               placeholder={placeholder}
               type="search"
               disabled={disabled}
               onFocus={() => setIsFocused(true)}
               onBlur={(_) => {
                  setIsFocused(false)
                  onBlur?.(name || "")
               }}
               value={inputValue}
               onChange={handleChange}
               onPaste={(e) => {
                  const pastedText = e.clipboardData
                     .getData("text")
                     .split("\n")
                     .filter(Boolean)
                  if (pastedText.length > 1) {
                     e.preventDefault()
                     onBulkPaste?.(pastedText)
                  }
               }}
            />
         </span>
      </div>
   )
}

export default SearchInput
